import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  isMessageNotificationForOpenChat,
  listNotificationsForUser,
  markMessageNotificationsReadForChat,
  dismissNotification,
  type AppNotification,
} from '../../lib/notifications';
import {
  getOpenMessageChat,
  NOTIFICATIONS_CHANGED_EVENT,
  OPEN_MESSAGE_CHAT_EVENT,
} from '../../lib/openMessageChat';
import { listPendingReferralsForParent } from '../../lib/therapistReferral';
import ParentTherapistReferralDecideModal from '../parent/ParentTherapistReferralDecideModal';

const REFERRAL_KIND = 'THERAPIST_REFERRAL';
const USER_MESSAGE_KIND = 'USER_MESSAGE';
const REFRESH_TTL_MS = 4_000;
const POLL_MS = 5_000;

function referralIdFromNotification(n: AppNotification): string | null {
  const payload = n.payload as { kind?: string; referralId?: string } | null | undefined;
  if (n.notificationType === 'THERAPIST_REFERRAL' && payload?.referralId) return payload.referralId;
  if (payload?.kind === REFERRAL_KIND && payload.referralId) return payload.referralId;
  return null;
}

function typeLabel(n: AppNotification): string | null {
  switch (n.notificationType) {
    case 'GOAL_CREATED':
      return 'Goal';
    case 'GOAL_COMPLETED':
      return 'Goal completed';
    case 'SESSION_COMPLETED':
      return 'Session';
    case 'CARE_PLAN_CREATED':
      return 'Care plan';
    case 'PATIENT_ASSIGNED':
      return 'Assignment';
    case 'PATIENT_REMOVED':
      return 'Assignment';
    case 'THERAPIST_REFERRAL':
    case 'REFERRAL_DECIDED':
      return 'Referral';
    case 'ASSESSMENT_ASSIGNED':
    case 'ASSESSMENT_COMPLETED':
      return 'Assessment';
    case 'ORG_INVITE_ACCEPTED':
    case 'ORG_MEMBER_JOINED':
      return 'Organization';
    case 'SYSTEM_ALERT':
      return 'Alert';
    default:
      return null;
  }
}

function isStoredReferralNotification(n: AppNotification): boolean {
  return Boolean(referralIdFromNotification(n));
}

function messageChatPathFromNotification(n: AppNotification): string | null {
  const payload = n.payload as {
    kind?: string;
    kidProfileId?: string | null;
    fromUserId?: string | null;
  } | null;

  if (payload?.kind === USER_MESSAGE_KIND) {
    const kidId = payload.kidProfileId || n.kidProfileId;
    const peerId = payload.fromUserId || n.fromUserId;
    if (kidId) {
      return peerId
        ? `/messages/${encodeURIComponent(kidId)}?with=${encodeURIComponent(peerId)}`
        : `/messages/${encodeURIComponent(kidId)}`;
    }
  }

  if (n.actionUrl && n.actionUrl.includes('/messages')) {
    return n.actionUrl;
  }
  return null;
}

/** Pending referrals stay until Accept/Decline. No per-row name lookups (modal loads details). */
async function listPendingReferralNotifications(parentUserId: string): Promise<AppNotification[]> {
  try {
    const pending = await listPendingReferralsForParent(parentUserId);
    return pending
      .map((row) => ({
        id: `pending-referral:${row.id}`,
        toUserId: parentUserId,
        fromUserId: row.therapistId,
        kidProfileId: row.kidProfileId,
        notificationType: 'THERAPIST_REFERRAL' as const,
        title: 'Therapist referral',
        body: 'A therapist referred another therapist for your child. Tap to Accept or Decline.',
        payload: {
          kind: REFERRAL_KIND,
          referralId: row.id,
          therapistId: row.therapistId,
          therapist2Id: row.therapist2Id,
          kidProfileId: row.kidProfileId,
        },
        readAt: null,
        createdAt: row.createdAt || new Date().toISOString(),
      }))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch {
    return [];
  }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [referralDecideId, setReferralDecideId] = useState<string | null>(null);
  const lastFetchedAt = useRef(0);
  const inFlight = useRef<Promise<void> | null>(null);
  const hasItemsRef = useRef(false);

  const refresh = useCallback(async (opts?: { force?: boolean }) => {
    if (!user?.id) {
      setUnread(0);
      setItems([]);
      hasItemsRef.current = false;
      return;
    }

    const now = Date.now();
    if (!opts?.force && lastFetchedAt.current > 0 && now - lastFetchedAt.current < REFRESH_TTL_MS) {
      return;
    }
    if (inFlight.current) return inFlight.current;

    if (!hasItemsRef.current) setLoading(true);

    const run = (async () => {
      try {
        const [storedRows, pendingReferralItems] = await Promise.all([
          listNotificationsForUser(user.id, { limit: 30 }),
          listPendingReferralNotifications(user.id),
        ]);

        // If this chat is open, clear matching message notifications (no bell while viewing).
        const openChat = getOpenMessageChat();
        if (openChat) {
          const pendingForOpen = storedRows.filter(
            (n) => !n.readAt && isMessageNotificationForOpenChat(n),
          );
          if (pendingForOpen.length > 0) {
            void markMessageNotificationsReadForChat(user.id, openChat);
          }
        }

        const otherStored = storedRows
          .filter((n) => !isStoredReferralNotification(n))
          // Only show unread — after click/open they leave the list
          .filter((n) => !n.readAt)
          // Hide message notifications for the chat currently open in this tab
          .filter((n) => !isMessageNotificationForOpenChat(n));

        const merged = [...pendingReferralItems, ...otherStored].sort((a, b) =>
          b.createdAt.localeCompare(a.createdAt),
        );
        const otherUnread = otherStored.length;
        const next = merged.slice(0, 30);

        setItems(next);
        hasItemsRef.current = next.length > 0;
        setUnread(pendingReferralItems.length + otherUnread);
        lastFetchedAt.current = Date.now();
      } catch {
        if (!hasItemsRef.current) {
          setUnread(0);
          setItems([]);
        }
      } finally {
        setLoading(false);
        inFlight.current = null;
      }
    })();

    inFlight.current = run;
    return run;
  }, [user?.id]);

  useEffect(() => {
    void refresh({ force: true });
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void refresh({ force: true });
    }, POLL_MS);
    const onFocus = () => void refresh({ force: true });
    const onChanged = () => void refresh({ force: true });
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    window.addEventListener(OPEN_MESSAGE_CHAT_EVENT, onChanged);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
      window.removeEventListener(OPEN_MESSAGE_CHAT_EVENT, onChanged);
    };
  }, [refresh]);

  if (!user?.id) return null;

  const handleClick = async (n: AppNotification) => {
    const referralId = referralIdFromNotification(n);
    const messagePath = messageChatPathFromNotification(n);
    const isVirtual = n.id.startsWith('pending-referral:');

    // Remove from bell immediately on click
    if (!isVirtual) {
      setItems((prev) => prev.filter((row) => row.id !== n.id));
      if (!n.readAt) setUnread((c) => Math.max(0, c - 1));
      void dismissNotification(n.id).catch(() => undefined);
    }

    setOpen(false);
    if (referralId) {
      setReferralDecideId(referralId);
      return;
    }
    if (messagePath) {
      navigate(messagePath);
      return;
    }
    if (n.actionUrl) navigate(n.actionUrl);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          void refresh({ force: true });
        }}
        className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 ? (
          <span className="absolute right-0.5 top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[1500] cursor-default"
            aria-label="Close notifications"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-[1501] mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">Notifications</p>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {loading && items.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-500">Loading…</p>
              ) : items.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-500">No notifications yet.</p>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => void handleClick(n)}
                    className={`w-full border-b border-slate-50 px-4 py-3 text-left transition hover:bg-slate-50 ${
                      !n.readAt ? 'bg-amber-50/70' : ''
                    }`}
                  >
                    <p className="text-sm font-medium text-slate-900">{n.title}</p>
                    {typeLabel(n) ? (
                      <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        {typeLabel(n)}
                      </p>
                    ) : null}
                    {n.body ? <p className="mt-0.5 line-clamp-2 text-xs text-slate-600">{n.body}</p> : null}
                    <p className="mt-1 text-[10px] text-slate-400">
                      {new Date(n.createdAt).toLocaleString()}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      ) : null}

      <ParentTherapistReferralDecideModal
        referralId={referralDecideId}
        onClose={() => setReferralDecideId(null)}
        onResolved={() => {
          lastFetchedAt.current = 0;
          void refresh({ force: true });
        }}
      />
    </div>
  );
}
